(() => {
  // v69 loses collection membership when gift sync refreshes the cart after
  // saving attributes. Keep that read complete until the app's v70 is released.
  const legacyGiftVersion = '2026-08-11-split-subscription-widget-targeting-v69';
  const originalFetch = window.fetch;
  const collectionLookups = new Map();
  const numericId = (id) => String(id || '').split('/').pop();
  const getCollections = (handle) => {
    if (!collectionLookups.has(handle)) {
      const url = new URL('/apps/everything-app', location.origin);
      url.searchParams.set('shop', window.Shopify?.shop || '');
      url.searchParams.set('product_handle', handle);
      collectionLookups.set(handle, originalFetch.call(window, url.href, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('Could not check free gift collection eligibility.');
          return response.json();
        })
        .then((payload) => {
          const memberships = payload.config?.variantCollectionIdsByVariantId;
          if (!memberships) throw new Error('Missing free gift collection eligibility.');
          return memberships;
        })
        .catch((error) => {
          collectionLookups.delete(handle);
          throw error;
        }));
    }
    return collectionLookups.get(handle);
  };

  window.fetch = async function (input, init) {
    const response = await originalFetch.apply(this, arguments);
    if (window.DiscountsAppDebug?.version !== legacyGiftVersion || !response.ok) return response;
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.origin);
    const method = String(init?.method || input?.method || 'GET').toUpperCase();
    if (method !== 'GET' || url.origin !== location.origin || !/\/cart\.js$/.test(url.pathname) || !url.searchParams.has('ts')) return response;

    const cart = await response.clone().json();
    if (!cart.items?.some((item) => item.properties?.__gift_offer_id)) return response;
    const mealLines = cart.items.filter((item) => !item.properties?.__gift_offer_id);
    const handles = [...new Set(mealLines.map((item) => item.handle || item.product_handle).filter(Boolean))];
    const memberships = Object.assign({}, ...await Promise.all(handles.map(getCollections)));
    for (const item of mealLines) {
      item.collectionIds = [...new Set([
        ...(item.collectionIds || []), ...(item.collection_ids || []),
        ...(memberships[numericId(item.variant_id)] || []),
      ].map(numericId))];
    }
    const headers = new Headers(response.headers);
    headers.delete('content-length');
    headers.delete('content-encoding');
    return new Response(JSON.stringify(cart), { status: response.status, statusText: response.statusText, headers });
  };

  const rowSelector = '.cp-cart-item';
  const controlSelector = '[data-discounts-app-line-subscription]';
  const cachedControls = new Map();
  let restoreTimer = null;

  const getLineKey = (row) => {
    if (!(row instanceof Element)) return '';

    const quantityInput = row.querySelector('[data-line-item-key]');
    return String(
      quantityInput?.dataset.lineItemKey ||
        row.dataset.cartItemKey ||
        row.getAttribute('data-cart-item-key') ||
        ''
    ).trim();
  };

  const rememberControl = (control, row = control?.closest?.(rowSelector)) => {
    if (!(control instanceof Element) || !(row instanceof Element)) return;

    const lineKey = getLineKey(row);
    if (!lineKey) return;

    control.dataset.cpRevliftLineKey = lineKey;
    cachedControls.set(lineKey, control);
  };

  const rememberConnectedControls = () => {
    document.querySelectorAll(`${rowSelector} ${controlSelector}`).forEach((control) => {
      rememberControl(control);
    });
  };

  const rememberRemovedSubtree = (node) => {
    if (!(node instanceof Element)) return;

    if (node.matches(controlSelector)) {
      const lineKey = String(node.dataset.cpRevliftLineKey || '').trim();
      if (lineKey) cachedControls.delete(lineKey);
      return;
    }

    if (node.matches(rowSelector)) {
      const control = node.querySelector(controlSelector);
      if (control) rememberControl(control, node);
      return;
    }

    node.querySelectorAll(`${rowSelector} ${controlSelector}`).forEach((control) => {
      rememberControl(control);
    });
  };

  const restoreMissingControls = () => {
    restoreTimer = null;
    rememberConnectedControls();

    document.querySelectorAll(rowSelector).forEach((row) => {
      if (row.querySelector(controlSelector)) return;

      const lineKey = getLineKey(row);
      const cachedControl = lineKey ? cachedControls.get(lineKey) : null;
      if (!(cachedControl instanceof Element) || cachedControl.isConnected) return;

      row.appendChild(cachedControl);
    });
  };

  const scheduleRestore = (delay = 120) => {
    if (restoreTimer) window.clearTimeout(restoreTimer);
    restoreTimer = window.setTimeout(restoreMissingControls, delay);
  };

  const start = () => {
    rememberConnectedControls();

    const headerCartLink = document.querySelector('.header__icons #cart-icon-bubble');
    const headerIcons = headerCartLink?.parentElement;
    const restoreHeaderCartLink = () => {
      if (!headerIcons?.isConnected || headerCartLink.isConnected) return;

      // The app's closed-drawer refresh replaces the link with Shopify's
      // section wrapper. Keep the original styled, accessible link and handlers.
      const replacement = headerIcons.querySelector('#shopify-section-cart-icon-bubble');
      if (!replacement) return;

      headerCartLink.innerHTML = replacement.innerHTML;
      replacement.replaceWith(headerCartLink);
    };

    const observer = new MutationObserver((records) => {
      restoreHeaderCartLink();
      let cartMarkupChanged = false;

      records.forEach((record) => {
        record.removedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (
            node.matches(rowSelector) ||
            node.matches(controlSelector) ||
            node.querySelector(`${rowSelector}, ${controlSelector}`)
          ) {
            rememberRemovedSubtree(node);
            cartMarkupChanged = true;
          }
        });

        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (
            node.matches(rowSelector) ||
            node.matches(controlSelector) ||
            node.querySelector(`${rowSelector}, ${controlSelector}`)
          ) {
            cartMarkupChanged = true;
          }
        });
      });

      if (cartMarkupChanged) scheduleRestore();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    [
      'cart:updated',
      'cart-updated',
      'cart:change',
      'cart:changed',
      'ajaxCart:updated',
      'discounts_app:cart_updated',
    ].forEach((eventName) => {
      document.addEventListener(eventName, () => scheduleRestore(180));
      window.addEventListener(eventName, () => scheduleRestore(180));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
